// /admin/orgs — list of every organization on the platform + an inline
// form to create a new one (ADR-0024).
//
// Read posture: TanStack Query with staleTime 60s; the list refreshes
// when a new org lands via useCreateAdminOrg's onSuccess invalidation.
//
// Create posture: name + recipe picker + optional RUT, simple confirm
// before submit (no typed-name ceremony per the user's call). Recipe
// is irreversible — the warning lives on the CTA label so it's visible
// at click time, not just in body copy.

import { useState } from "react";
import { Link } from "react-router-dom";
import { EmptyState } from "../../components/EmptyState";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skel } from "../../components/Skel";
import { SkelTable } from "../../components/skeleton/SkelTable";
import { ApiError } from "../../lib/api/error";
import { useQueryView } from "../../lib/loading/useQueryView";
import { useAdminOrgs, useAdminRecipes, useCreateAdminOrg } from "./queries";

const ORG_COLUMNS = ["Nombre", "Recipe", "RUT", "Estado", "Creada"];

export function AdminOrgs() {
  const orgsQuery = useAdminOrgs();
  // The subtitle's count follows the same view as the table below: a
  // bar in the real line box while loading, nothing on an error (the
  // body owns the error panel), never a «Cargando…» line.
  const countView = useQueryView(orgsQuery);
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="page">
      <div className="page-pad">
        <header className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">Platform admin</div>
            <h1 className="page-header__title">Organizaciones</h1>
            <p className="page-header__sub">
              {countView.kind === "ready" ? (
                `${countView.data.orgs.length} en la plataforma.`
              ) : countView.kind === "empty" ? (
                "0 en la plataforma."
              ) : countView.kind === "loading" ? (
                <Skel variant="text" width={140} />
              ) : null}
            </p>
          </div>
          <div className="page-header__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? "Cancelar" : "Nueva organización"}
            </button>
          </div>
        </header>

        {showForm && <NewOrgForm onDone={() => setShowForm(false)} />}

        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Todas las organizaciones</h2>
          </div>
          <QueryBoundary
            query={orgsQuery}
            label="las organizaciones"
            // Rows that fill a 1280×800 viewport under the header.
            skeleton={<SkelTable rows={10} cols={ORG_COLUMNS.length} headers={ORG_COLUMNS} />}
            isEmpty={(d) => d.orgs.length === 0}
            empty={
              <EmptyState
                title="Sin organizaciones"
                description="Crea la primera con el botón de arriba."
              />
            }
          >
            {(data) => (
              <div className="panel__body--flush">
                <table className="dtable" aria-label="Organizaciones">
                  <thead>
                    <tr>
                      {ORG_COLUMNS.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.orgs.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <Link to={`/admin/orgs/${o.id}`}>
                            <span className="cell-strong">{o.name}</span>
                          </Link>
                        </td>
                        <td>
                          <code>{o.recipeSlug}</code>
                        </td>
                        <td>{o.rut ?? "—"}</td>
                        <td>
                          {o.active ? (
                            <span className="st-pill st-pill--ok">Activa</span>
                          ) : (
                            <span className="st-pill st-pill--idle">Inactiva</span>
                          )}
                        </td>
                        <td>
                          <span className="cell-meta">
                            {new Date(o.createdAt).toLocaleDateString("es-CL")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </QueryBoundary>
        </section>
      </div>
    </div>
  );
}

function NewOrgForm({ onDone }: { onDone: () => void }) {
  const recipes = useAdminRecipes();
  const create = useCreateAdminOrg();
  const [name, setName] = useState("");
  const [recipeSlug, setRecipeSlug] = useState("");
  const [rut, setRut] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && recipeSlug.length > 0 && !create.isPending;

  async function handleConfirm() {
    setError(null);
    try {
      await create.mutateAsync({
        name: trimmedName,
        recipeSlug,
        ...(rut.trim() ? { rut: rut.trim() } : {}),
      });
      setName("");
      setRecipeSlug("");
      setRut("");
      setConfirming(false);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos crear la organización.");
    }
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2 className="panel__title">Nueva organización</h2>
      </div>
      <div style={{ padding: "16px 18px" }}>
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        <div className="field">
          <label className="field__label" htmlFor="new-org-name">
            Nombre
          </label>
          <input
            id="new-org-name"
            className="input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Falabella Retail"
            required
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="new-org-recipe">
            Recipe (vertical)
          </label>
          <select
            id="new-org-recipe"
            className="select"
            value={recipeSlug}
            onChange={(e) => setRecipeSlug(e.target.value)}
            required
          >
            <option value="">Selecciona un recipe…</option>
            {recipes.data?.recipes.map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.slug} — {r.organizationLabel}
              </option>
            ))}
          </select>
          <p className="cell-meta" style={{ marginTop: 6, fontSize: "var(--t-12)" }}>
            No se puede cambiar después de creada la organización.
          </p>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="new-org-rut">
            RUT (opcional)
          </label>
          <input
            id="new-org-rut"
            className="input"
            type="text"
            value={rut}
            onChange={(e) => setRut(e.target.value)}
            placeholder="76.123.456-7"
          />
        </div>

        {!confirming ? (
          <div className="stack-gap" style={{ flexDirection: "row" }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!canSubmit}
              onClick={() => setConfirming(true)}
            >
              {recipeSlug ? `Crear con recipe "${recipeSlug}"` : "Crear organización"}
            </button>
          </div>
        ) : (
          <div
            role="alertdialog"
            aria-labelledby="confirm-title"
            style={{
              padding: 14,
              border: "1px solid var(--line)",
              borderRadius: 8,
              background: "var(--paper-2)",
            }}
          >
            <p id="confirm-title" style={{ marginTop: 0 }}>
              ¿Crear <strong>{trimmedName}</strong> con recipe <code>{recipeSlug}</code>?
            </p>
            <p className="cell-meta" style={{ marginBottom: 12, fontSize: "var(--t-12)" }}>
              Esta acción mintea un tenant nuevo en la plataforma y no se puede deshacer desde el
              panel.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirming(false)}
                disabled={create.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void handleConfirm()}
                disabled={create.isPending}
              >
                {create.isPending ? "Creando…" : "Sí, crear"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
